/**
 * ttsQueue.ts — sentence-chunked TTS for the manual "🔊 speak" button.
 *
 * Audio playback is delegated to globalTts (single AudioContext, single live
 * source). This guarantees that pressing speak while auto-speak is reading,
 * or pressing speak twice quickly, never produces overlapping voices.
 */
import { unlockAudio as unlockGlobal, enqueue, cancelAll } from "./globalTts";

export type TtsAbortHandle = { cancelled: boolean };

const SPLIT_RE = /(?<=[.!?…;,])\s+/;
const FIRST_CHUNK_MAX = 80;
const CHUNK_MAX = 180;

export function unlockAudio() { unlockGlobal(); }

export function splitForTts(text: string): string[] {
  const t = (text || "").trim();
  if (!t) return [];
  const parts = t.split(SPLIT_RE).map((s) => s.trim()).filter(Boolean);
  if (!parts.length) return [t.slice(0, CHUNK_MAX)];

  const chunks: string[] = [];
  let buf = "";
  const maxFor = () => (chunks.length === 0 ? FIRST_CHUNK_MAX : CHUNK_MAX);

  for (const s of parts) {
    if (!buf) {
      if (s.length > maxFor()) chunks.push(...hardSplit(s, maxFor()));
      else buf = s;
      continue;
    }
    if ((buf + " " + s).length <= maxFor()) {
      buf += " " + s;
    } else {
      chunks.push(buf);
      if (s.length > maxFor()) {
        chunks.push(...hardSplit(s, maxFor()));
        buf = "";
      } else {
        buf = s;
      }
    }
  }
  if (buf) chunks.push(buf);
  return chunks;
}

function hardSplit(s: string, max: number): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < s.length) {
    let end = Math.min(i + max, s.length);
    if (end < s.length) {
      const sp = s.lastIndexOf(" ", end);
      if (sp > i + 30) end = sp;
    }
    out.push(s.slice(i, end).trim());
    i = end;
  }
  return out.filter(Boolean);
}

export async function playTtsQueue(
  text: string,
  _audioRef: { current: HTMLAudioElement | null },
  abort: TtsAbortHandle,
): Promise<void> {
  const chunks = splitForTts(text);
  if (!chunks.length) return;
  // Cancel anything currently playing (auto-speak, previous click, etc).
  cancelAll();
  unlockGlobal();
  for (const c of chunks) {
    if (abort.cancelled) { cancelAll(); return; }
    enqueue(c);
  }
  // Resolve when the queue drains or this call is cancelled.
  await new Promise<void>((resolve) => {
    const t = setInterval(() => {
      if (abort.cancelled) { cancelAll(); clearInterval(t); resolve(); return; }
      // Heuristic: when globalTts is no longer playing, we are done.
      // Imported lazily to avoid circular issues.
      void import("./globalTts").then(({ isPlaying }) => {
        if (!isPlaying()) { clearInterval(t); resolve(); }
      });
    }, 200);
  });
}
